import React, { useState } from 'react';
import { Button } from './ui/button';
import { Plus, Trash2, Edit2, Check, X } from 'lucide-react';

const SpinningWheel = ({ names = [], onNamesChange, editable = false }) => {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winner, setWinner] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showNameList, setShowNameList] = useState(false);

  const colors = [
    '#dc2626', // red
    '#2563eb', // blue
    '#16a34a', // green
    '#ea580c', // orange
    '#7c3aed', // purple
    '#ca8a04', // yellow
    '#0891b2', // cyan
    '#be123c', // rose
  ];

  const spinWheel = () => {
    if (spinning || names.length === 0) return;

    setSpinning(true);
    setWinner('');

    // Random spins between 5-10 full rotations + random angle
    const spins = 5 + Math.floor(Math.random() * 5);
    const randomAngle = Math.floor(Math.random() * 360);
    const totalRotation = rotation + (spins * 360) + randomAngle;

    setRotation(totalRotation);

    // Calculate winner after animation
    setTimeout(() => {
      const segmentAngle = 360 / names.length;
      const normalizedRotation = totalRotation % 360;
      const winnerIndex = Math.floor((360 - normalizedRotation + segmentAngle / 2) / segmentAngle) % names.length;
      setWinner(names[winnerIndex]);
      setSpinning(false);
    }, 4000);
  };

  const addName = () => {
    if (onNamesChange) {
      onNamesChange([...names, `Person ${names.length + 1}`]);
    }
  };

  const removeName = (index) => {
    if (names.length <= 2) {
      alert('Wheel must have at least 2 names');
      return;
    }
    if (onNamesChange) {
      onNamesChange(names.filter((_, i) => i !== index));
    }
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditValue(names[index]);
  };

  const saveEdit = () => {
    if (editValue.trim() && onNamesChange) {
      const newNames = [...names];
      newNames[editingIndex] = editValue.trim();
      onNamesChange(newNames);
    }
    setEditingIndex(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  if (names.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-gray-500">No names on the wheel</p>
        {editable && onNamesChange && (
          <Button onClick={addName} className="bg-green-600 hover:bg-green-700 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Add First Name
          </Button>
        )}
      </div>
    );
  }

  const segmentAngle = 360 / names.length;

  return (
    <div className="flex flex-col items-center justify-center gap-6 h-full">
      {/* Edit Names Button */}
      {editable && onNamesChange && (
        <div className="flex gap-2">
          <Button
            onClick={() => setShowNameList(!showNameList)}
            variant="outline"
            size="sm"
            className="bg-purple-50"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            {showNameList ? 'Hide Names' : 'Edit Names'}
          </Button>
          <Button
            onClick={addName}
            variant="outline"
            size="sm"
            className="bg-green-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Name
          </Button>
        </div>
      )}

      {/* Name List Editor */}
      {showNameList && editable && (
        <div className="bg-white rounded-xl shadow-lg p-4 w-80 max-h-64 overflow-y-auto border-2 border-purple-200">
          <h3 className="font-bold text-gray-900 mb-3 flex items-center justify-between">
            <span>Edit Names ({names.length})</span>
            <button onClick={() => setShowNameList(false)} className="text-gray-500 hover:text-gray-700">×</button>
          </h3>
          <div className="space-y-2">
            {names.map((name, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                {editingIndex === index ? (
                  <>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                      className="flex-1 px-2 py-1 border border-purple-400 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                      autoFocus
                    />
                    <button
                      onClick={saveEdit}
                      className="p-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{name}</span>
                    <button
                      onClick={() => startEdit(index)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {names.length > 2 && (
                      <button
                        onClick={() => removeName(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative w-80 h-80">
        {/* Spinning Wheel */}
        <svg
          width="320"
          height="320"
          viewBox="0 0 400 400"
          className="drop-shadow-2xl"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
          }}
        >
          {/* Draw segments */}
          {names.map((name, index) => {
            const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
            const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
            const largeArc = segmentAngle > 180 ? 1 : 0;

            const x1 = 200 + 180 * Math.cos(startAngle);
            const y1 = 200 + 180 * Math.sin(startAngle);
            const x2 = 200 + 180 * Math.cos(endAngle);
            const y2 = 200 + 180 * Math.sin(endAngle);

            const textAngle = index * segmentAngle + segmentAngle / 2 - 90;
            const textRadius = 130;
            const textX = 200 + textRadius * Math.cos((textAngle) * Math.PI / 180);
            const textY = 200 + textRadius * Math.sin((textAngle) * Math.PI / 180);

            return (
              <g key={index}>
                {/* Segment */}
                <path
                  d={`M 200 200 L ${x1} ${y1} A 180 180 0 ${largeArc} 1 ${x2} ${y2} Z`}
                  fill={colors[index % colors.length]}
                  stroke="#ffffff"
                  strokeWidth="3"
                />
                {/* Text */}
                <text
                  x={textX}
                  y={textY}
                  fill="white"
                  fontSize="14"
                  fontWeight="bold"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                >
                  {name}
                </text>
              </g>
            );
          })}
          
          {/* Center circle */}
          <circle cx="200" cy="200" r="30" fill="white" stroke="#e5e7eb" strokeWidth="4" />
          <text x="200" y="200" textAnchor="middle" dominantBaseline="middle" fontSize="16" fontWeight="bold" fill="#374151">
            SPIN
          </text>
        </svg>

        {/* Pointer */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10"
          style={{ width: 0, height: 0, borderLeft: '15px solid transparent', borderRight: '15px solid transparent', borderTop: '30px solid #dc2626' }}
        />
      </div>

      {/* Spin Button */}
      <Button
        onClick={spinWheel}
        disabled={spinning}
        size="lg"
        className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6"
      >
        {spinning ? 'Spinning...' : 'Spin the Wheel'}
      </Button>

      {/* Winner Display */}
      {winner && (
        <div className="text-center animate-bounce">
          <p className="text-2xl font-bold text-red-600">🎉 Winner: {winner}! 🎉</p>
        </div>
      )}
    </div>
  );
};

export default SpinningWheel;
